'use client';

import React, { Fragment, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Listbox, Transition } from '@headlessui/react';
import { ProductVariant } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { twJoin } from 'tailwind-merge';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import Button from '@/ui/components/shared/Button';
import { buildTrackingId } from '@/utils/tracking-helper';
import { DynamicComparisonTableTile } from '../../types/DynamicComparisonTableTile';
import { extractColors } from './utils/extractColors';

interface ProductInfoProps {
  tile: DynamicComparisonTableTile;
  initialProductIdx?: number | undefined;
  trackingPrefix?: string;
}

export default function ProductInfo({
  tile,
  initialProductIdx,
  trackingPrefix,
}: Readonly<ProductInfoProps>) {
  const { formatCurrency } = useCurrencyFormatter();
  const products = tile.products.items;
  const productIdx = initialProductIdx ? initialProductIdx - 1 : 0;
  // verify products in products are typename of Product
  const [selectedProduct, setSelectedProduct] = useState(
    products?.[productIdx]?.__typename === 'Product' ? products?.[productIdx] : undefined,
  );
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);

  if (!selectedProduct || !products || selectedProduct.__typename !== 'Product') {
    return null;
  }
  const comparisonFeaturesContent = selectedProduct.comparisonFeatures?.content;
  const productFeatures =
    comparisonFeaturesContent?.__typename === 'ItemRelationsContent'
      ? comparisonFeaturesContent.items
      : [];
  const colors = extractColors(selectedProduct);
  const selectedProductVariants = selectedProduct.variants;
  const currentImage = selectedProductVariants?.[selectedVariantIdx];

  const buyButtonTrackingId = buildTrackingId(trackingPrefix, selectedProduct.name);

  return (
    <div>
      <Listbox
        value={selectedProduct}
        onChange={(product) => {
          setSelectedProduct(product);
          setSelectedVariantIdx(0);
        }}
      >
        <div className="relative">
          <Listbox.Button className="relative flex w-full items-center justify-between gap-1 border-b border-neutral-15 py-2 sm:gap-2 sm:rounded-xs sm:border sm:px-3 sm:py-4">
            <span className="block text-sm font-bold sm:text-base md:text-md">
              {selectedProduct.name}
            </span>
            <ChevronDownIcon
              className="h-4 w-4 text-primary-100 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
          </Listbox.Button>
          <Transition
            as={Fragment}
            enterFrom=" opacity-0"
            enter=" transition ease-in duration-100"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className=" absolute mt-1 max-h-xs w-full overflow-auto rounded-xs border border-neutral-15 bg-white">
              {products.map((product, idx: number) => (
                <Listbox.Option
                  key={idx}
                  className={({ active }) =>
                    `relative select-none py-2 pl-5 pr-1 text-sm duration-100 ease-in sm:pl-10 sm:pr-4 sm:text-base ${
                      active && 'bg-neutral-5'
                    }`
                  }
                  value={product}
                >
                  {({ selected }) => (
                    <>
                      <span className={twJoin('block', selected && 'font-bold')}>
                        {product.name}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-px text-primary-100 sm:pl-3">
                          <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      <div className=" mx-auto my-4 h-37 w-full sm:my-8 sm:h-50 md:w-50">
        <OptimizedImage
          imageComponent={currentImage as ProductVariant}
          width={200}
          height={400}
          className="mx-auto h-full w-auto object-contain"
          sizes="(max-width: 640px) 150px, (max-width: 768px) 200px, 330px"
        />
      </div>
      <div className=" flex justify-center gap-2 sm:gap-3">
        {colors.length > 0 &&
          colors.map((color, index: number) => (
            <div key={index} className="flex flex-col items-center">
              <button
                type="button"
                aria-label={`${color.name}`}
                className={twJoin(
                  'h-5 w-5 rounded-full border border-neutral-10 shadow-inner sm:h-8 sm:w-8 md:h-10 md:w-10',
                  color.variantIndex === selectedVariantIdx
                    ? 'outline outline-1 outline-offset-2 outline-primary-100'
                    : ' outline-none',
                )}
                style={{ backgroundColor: color.hexCode }}
                onClick={() => setSelectedVariantIdx(color.variantIndex)}
              />
              <span className=" mt-2 text-center text-xs sm:text-sm">{color.name}</span>
            </div>
          ))}
      </div>
      <div className="my-6 w-full border-t border-neutral-10 border-opacity-0 sm:my-8 sm:border-opacity-100" />
      <div className=" mx-auto max-w-[180px] space-y-10 text-center sm:space-y-12">
        {productFeatures?.map((feature, index: number) => {
          const featureComponents = feature.__typename === 'Document' ? feature.components : [];
          const imageContent = featureComponents?.find(
            (component) => component.id === 'icon',
          )?.content;
          const image = imageContent?.__typename === 'ImageContent' ? imageContent : undefined;
          const textContent = featureComponents?.find(
            (component) => component.id === 'text',
          )?.content;
          const text = textContent?.__typename === 'RichTextContent' ? textContent : undefined;
          const textInParenthesesContent = featureComponents?.find(
            (component) => component.id === 'name',
          )?.content;
          const textInParentheses =
            textInParenthesesContent?.__typename === 'SingleLineContent'
              ? textInParenthesesContent.text
              : undefined;

          return (
            <div
              key={index}
              className=" flex flex-col items-center justify-center text-sm sm:text-base"
            >
              <OptimizedImage
                imageComponent={image}
                width={48}
                height={48}
                className=" mb-3 h-10 w-auto sm:h-12"
                sizes="48px"
              />
              <RichTextRenderer blocks={text} />
              {textInParentheses && <span className=" text-neutral-30">{textInParentheses}</span>}
            </div>
          );
        })}
      </div>
      <div className=" my-6 w-full border-t border-neutral-10 sm:my-8" />
      <p className=" text-center font-bold">
        {tile['price-text']?.text}{' '}
        {selectedProductVariants && (
          <span>
            {formatCurrency(
              selectedProductVariants.find((variant) => variant?.isDefault)?.priceVariant,
              {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              },
            )}
          </span>
        )}
      </p>
      {selectedProduct.path && tile['button-text']?.text && (
        <Button
          href={selectedProduct.path}
          className=" mx-auto mt-6 w-fit"
          data-tile-element-id={buyButtonTrackingId}
        >
          {tile['button-text']?.text}
        </Button>
      )}
    </div>
  );
}
